import React, { useMemo, memo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { tomorrow } from "react-syntax-highlighter/dist/esm/styles/prism";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import { Box, Paper, Typography, Chip } from "@mui/material";
import { styled } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const TOKEN_RE = /%%\s*(\d+)\s*%%/g;
const PAREN_TOKEN_RE = /\(\s*%%\s*\d+\s*%%\s*\)/g; // e.g., (%% 1 %%)
const PAREN_I_RE = /\(\s*i\s*\)/gi; // e.g., (i)
const BOLD_PAREN_TOKEN_RE = /\*\*\s*\(\s*%%\s*\d+\s*%%\s*\)\s*\*\*/g; // e.g., **(%%1%%)**
const BOLD_TOKEN_RE = /\*\*\s*%%\s*\d+\s*%%\s*\*\*/g; // e.g., **%%1%%**

function stripContextMarkers(text) {
  if (!text) return "";
  return text
    .replace(BOLD_PAREN_TOKEN_RE, "")
    .replace(BOLD_TOKEN_RE, "")
    .replace(PAREN_TOKEN_RE, "")
    .replace(PAREN_I_RE, "")
    .replace(TOKEN_RE, "")
    // Cleanup: remove empty bold markers created by stripping tokens (e.g., "** **")
    .replace(/\*\*\s*\*\*/g, "");
}

// Styled components for better markdown rendering
const StyledTable = styled("table")(({ theme }) => ({
  width: "100%",
  borderCollapse: "collapse",
  margin: "20px 0",
  fontSize: "0.9rem",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
  border: "1px solid rgba(24, 116, 210, 0.08)",
  "& th, & td": {
    border: "1px solid rgba(24, 116, 210, 0.08)",
    padding: "12px 16px",
    textAlign: "left",
  },
  "& th": {
    backgroundColor: "rgba(24, 116, 210, 0.04)",
    fontWeight: 600,
    color: "#002855",
  },
  "& tr:nth-of-type(even)": {
    backgroundColor: "rgba(24, 116, 210, 0.02)",
  },
  "& tr:hover": {
    backgroundColor: "rgba(24, 116, 210, 0.04)",
  },
}));

const StyledBlockquote = styled("blockquote")(({ theme }) => ({
  borderLeft: "4px solid rgba(24, 116, 210, 1)",
  margin: "20px 0",
  padding: "16px 20px",
  backgroundColor: "rgba(24, 116, 210, 0.04)",
  borderRadius: "0 8px 8px 0",
  fontStyle: "italic",
  border: "1px solid rgba(24, 116, 210, 0.08)",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
  "& p": {
    margin: 0,
    color: "#333333",
  },
}));

const CodeBlockContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  margin: "20px 0",
  borderRadius: "12px",
  overflow: "hidden",
  border: "1px solid rgba(24, 116, 210, 0.08)",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
  "& .copy-button": {
    position: "absolute",
    top: "12px",
    right: "12px",
    zIndex: 1,
    opacity: 0,
    transition: "opacity 0.2s",
  },
  "&:hover .copy-button": {
    opacity: 1,
  },
}));

const InlineCode = styled("code")(({ theme }) => ({
  backgroundColor: "rgba(24, 116, 210, 0.08)",
  padding: "3px 8px",
  borderRadius: "6px",
  fontSize: "0.9em",
  fontFamily: "Monaco, Consolas, 'Courier New', monospace",
  color: "rgba(24, 116, 210, 1)",
  border: "1px solid rgba(24, 116, 210, 0.12)",
}));

const StyledList = styled("ul")(({ theme }) => ({
  paddingLeft: "28px",
  margin: "16px 0",
  "& li": {
    marginBottom: "8px",
    lineHeight: "1.6",
    color: "#333333",
  },
}));

const StyledOrderedList = styled("ol")(({ theme }) => ({
  paddingLeft: "28px",
  margin: "16px 0",
  "& li": {
    marginBottom: "8px",
    lineHeight: "1.6",
    color: "#333333",
  },
}));

// Callout/Admonition styles
const CalloutContainer = styled(Box)(({ severity = "info" }) => {
  const colorMap = {
    info: { border: "rgba(24, 116, 210, 1)", bg: "rgba(24, 116, 210, 0.04)" },
    tip: { border: "#2e7d32", bg: "rgba(46, 125, 50, 0.04)" },
    warning: { border: "#ed6c02", bg: "rgba(237, 108, 2, 0.04)" },
    danger: { border: "#d32f2f", bg: "rgba(211, 47, 47, 0.04)" },
    important: { border: "#6a1b9a", bg: "rgba(106, 27, 154, 0.04)" },
  };
  const c = colorMap[severity] || colorMap.info;
  return {
    borderLeft: `4px solid ${c.border}`,
    backgroundColor: c.bg,
    padding: "16px 20px",
    margin: "20px 0",
    borderRadius: "0 8px 8px 0",
    border: `1px solid ${c.border}20`,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
  };
});

const CalloutTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontWeight: 600,
  marginBottom: 6,
});

// Copy functionality component
const CopyButton = ({ text, onCopy }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onCopy?.();
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <Tooltip title={copied ? "Copied!" : "Copy code"}>
      <IconButton
        size="small"
        onClick={handleCopy}
        className="copy-button"
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.9)",
          },
        }}
      >
        {copied ? (
          <CheckIcon fontSize="small" sx={{ color: "#4caf50" }} />
        ) : (
          <ContentCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
};

function tokenizeTextToNodes(text, contextArray, handleContextModalOpen) {
  const resolveContextByToken = (token) => {
    const contexts = Array.isArray(contextArray) ? contextArray : [];
    const numericToken = Number(token);
    if (!Number.isFinite(numericToken)) return null;

    const byId = contexts.find((ctx) => {
      const contextId = Number(ctx?.context_id);
      const contextIdx = Number(ctx?.context_idx);
      const chunkIdx = Number(ctx?.chunk_idx);
      return (
        (Number.isFinite(contextId) && contextId === numericToken) ||
        (Number.isFinite(contextIdx) && contextIdx === numericToken) ||
        (Number.isFinite(chunkIdx) && chunkIdx === numericToken)
      );
    });
    return byId || null;
  };

  const nodes = [];
  let lastIndex = 0;
  let match;

  TOKEN_RE.lastIndex = 0;

  while ((match = TOKEN_RE.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    const tokenValue = Number(match[1]);

    if (start > lastIndex) {
      nodes.push(text.slice(lastIndex, start));
    }

    const src = resolveContextByToken(tokenValue);
    const key = `ctx-${start}-${tokenValue}`;
    nodes.push(
      <Tooltip key={key} title={src ? "Show context" : "No context"}>
        <span style={{ display: "inline-flex", verticalAlign: "middle" }}>
          <IconButton
            size="small"
            className="context-marker-button"
            sx={{
              width: 20,
              height: 20,
              p: 0,
              border: "1px solid rgba(24, 116, 210, 0.2)",
              borderRadius: "50%",
              ml: 0.5,
              mr: 0.5,
              backgroundColor: "rgba(24, 116, 210, 0.08)",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                backgroundColor: "rgba(24, 116, 210, 0.12)",
                transform: "scale(1.1)",
                boxShadow: "0 2px 8px rgba(24, 116, 210, 0.2)",
              },
              "&:disabled": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
              },
            }}
            onClick={() => src && handleContextModalOpen(src)}
            disabled={!src}
          >
            <InfoIcon sx={{ fontSize: 12, color: "rgba(24, 116, 210, 1)" }} />
          </IconButton>
        </span>
      </Tooltip>
    );

    lastIndex = end;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

const wrap =
  (Tag, contextArray, handleContextModalOpen) =>
  ({ node, children, ...props }) =>
    (
      <Tag {...props}>
        {React.Children.map(children, (child, i) =>
          typeof child === "string"
            ? tokenizeTextToNodes(child, contextArray, handleContextModalOpen)
            : child
        )}
      </Tag>
    );

const CustomMarkdown = memo(
  ({
    markdown,
    contextArray,
    handleContextModalOpen,
    showInlineContext = true,
  }) => {
    const headingFontSizes = {
      h1: "2.1rem",
      h2: "1.8rem",
      h3: "1.55rem",
      h4: "1.35rem",
      h5: "1.2rem",
      h6: "1.1rem",
    };

    const HeadingFactory =
      (variant, componentTag) =>
      ({ children, ...props }) =>
        (
          <Typography
            variant={variant}
            component={componentTag}
            sx={{
              mt: 2,
              mb: 1.25,
              fontWeight: 700,
              lineHeight: 1.25,
              color: "#000000",
              fontSize: headingFontSizes[componentTag] || undefined,
            }}
            {...props}
          >
            {React.Children.map(children, (child) =>
              typeof child === "string"
                ? tokenizeTextToNodes(
                    child,
                    contextArray,
                    handleContextModalOpen
                  )
                : child
            )}
          </Typography>
        );

    const Callout = ({ severity = "info", title, children }) => {
      const Icon =
        severity === "tip"
          ? TipsAndUpdatesIcon
          : severity === "warning"
          ? WarningAmberIcon
          : severity === "danger"
          ? ErrorOutlineIcon
          : severity === "important"
          ? InfoOutlinedIcon
          : InfoOutlinedIcon;
      return (
        <CalloutContainer severity={severity}>
          {title && (
            <CalloutTitle>
              <Icon fontSize="small" />
              <span>{title}</span>
            </CalloutTitle>
          )}
          <Box>{children}</Box>
        </CalloutContainer>
      );
    };

    const parseCallout = (children) => {
      // Detect leading pattern: [!NOTE] / [!TIP] / [!WARNING] / [!IMPORTANT] / [!ERROR]
      const first = React.Children.toArray(children)[0];
      const firstStr = typeof first === "string" ? first : null;
      if (!firstStr) return null;
      const m = firstStr.match(
        /^\s*\[!(NOTE|TIP|WARNING|IMPORTANT|ERROR)\]\s*(.*)$/i
      );
      if (!m) return null;
      const tag = m[1].toUpperCase();
      const restFirst = m[2] || "";
      const severityMap = {
        NOTE: "info",
        TIP: "tip",
        WARNING: "warning",
        IMPORTANT: "important",
        ERROR: "danger",
      };
      const severity = severityMap[tag] || "info";
      const newChildren = [
        restFirst,
        ...React.Children.toArray(children).slice(1),
      ];
      return {
        severity,
        children: newChildren,
        title: tag[0] + tag.slice(1).toLowerCase(),
      };
    };

    const CodeBlock = ({ language, codeString, ...props }) => {
      const [expanded, setExpanded] = useState(false);
      const numLines = codeString.split("\n").length;
      const shouldCollapse = numLines > 16 || codeString.length > 1200;
      return (
        <CodeBlockContainer>
          <SyntaxHighlighter
            style={tomorrow}
            language={language}
            PreTag="div"
            customStyle={{
              margin: 0,
              borderRadius: "8px",
              fontSize: "0.9rem",
              maxHeight: shouldCollapse && !expanded ? 320 : "none",
              overflow: "auto",
              position: "relative",
            }}
            {...props}
          >
            {codeString}
          </SyntaxHighlighter>
          <CopyButton text={codeString} />
          {shouldCollapse && (
            <Box
              className="code-fade-overlay"
              sx={{ display: expanded ? "none" : "block" }}
            />
          )}
          {shouldCollapse && (
            <Box textAlign="center" sx={{ mt: 0.5 }}>
              <Chip
                size="small"
                label={expanded ? "Show less" : "Show more"}
                onClick={() => setExpanded((v) => !v)}
                sx={{ cursor: "pointer" }}
              />
            </Box>
          )}
        </CodeBlockContainer>
      );
    };

    const components = useMemo(
      () => ({
        // Headings
        h1: HeadingFactory("h3", "h1"),
        h2: HeadingFactory("h4", "h2"),
        h3: HeadingFactory("h5", "h3"),
        h4: HeadingFactory("h6", "h4"),
        h5: HeadingFactory("subtitle1", "h5"),
        h6: HeadingFactory("subtitle2", "h6"),

        // Paragraphs
        p: ({ children, ...props }) => {
          const callout = parseCallout(children);
          if (callout) {
            return (
              <Callout severity={callout.severity} title={callout.title}>
                <Typography variant="body1" sx={{ mb: 0, lineHeight: 1.6 }}>
                  {React.Children.map(callout.children, (child) =>
                    typeof child === "string"
                      ? tokenizeTextToNodes(
                          child,
                          contextArray,
                          handleContextModalOpen
                        )
                      : child
                  )}
                </Typography>
              </Callout>
            );
          }
          return (
            <Typography
              variant="body1"
              sx={{ mb: 1.5, lineHeight: 1.6, color: "#000000" }}
              {...props}
            >
              {React.Children.map(children, (child) =>
                typeof child === "string"
                  ? tokenizeTextToNodes(
                      child,
                      contextArray,
                      handleContextModalOpen
                    )
                  : child
              )}
            </Typography>
          );
        },

        // Lists
        ul: ({ children, ...props }) => (
          <StyledList {...props}>
            {React.Children.map(children, (child) =>
              typeof child === "string"
                ? tokenizeTextToNodes(
                    child,
                    contextArray,
                    handleContextModalOpen
                  )
                : child
            )}
          </StyledList>
        ),
        ol: ({ children, ...props }) => (
          <StyledOrderedList {...props}>
            {React.Children.map(children, (child) =>
              typeof child === "string"
                ? tokenizeTextToNodes(
                    child,
                    contextArray,
                    handleContextModalOpen
                  )
                : child
            )}
          </StyledOrderedList>
        ),
        li: ({ children, ...props }) => (
          <li {...props}>
            {React.Children.map(children, (child) =>
              typeof child === "string"
                ? tokenizeTextToNodes(
                    child,
                    contextArray,
                    handleContextModalOpen
                  )
                : child
            )}
          </li>
        ),

        // Blockquotes
        blockquote: ({ children, ...props }) => {
          const callout = parseCallout(children);
          if (callout) {
            return (
              <Callout severity={callout.severity} title={callout.title}>
                {React.Children.map(callout.children, (child) =>
                  typeof child === "string"
                    ? tokenizeTextToNodes(
                        child,
                        contextArray,
                        handleContextModalOpen
                      )
                    : child
                )}
              </Callout>
            );
          }
          return (
            <StyledBlockquote {...props}>
              {React.Children.map(children, (child) =>
                typeof child === "string"
                  ? tokenizeTextToNodes(
                      child,
                      contextArray,
                      handleContextModalOpen
                    )
                  : child
              )}
            </StyledBlockquote>
          );
        },

        // Tables
        table: ({ children, ...props }) => (
          <Box sx={{ overflowX: "auto", mb: 2 }}>
            <StyledTable {...props}>
              {React.Children.map(children, (child) =>
                typeof child === "string"
                  ? tokenizeTextToNodes(
                      child,
                      contextArray,
                      handleContextModalOpen
                    )
                  : child
              )}
            </StyledTable>
          </Box>
        ),
        thead: wrap("thead", contextArray, handleContextModalOpen),
        tbody: wrap("tbody", contextArray, handleContextModalOpen),
        tr: wrap("tr", contextArray, handleContextModalOpen),
        th: wrap("th", contextArray, handleContextModalOpen),
        td: wrap("td", contextArray, handleContextModalOpen),

        // Code blocks
        code: ({ node, inline, className, children, ...props }) => {
          const match = /language-(\w+)/.exec(className || "");
          const language = match ? match[1] : "";
          const codeString = String(children).replace(/\n$/, "");

          if (!inline && language) {
            return (
              <CodeBlock
                language={language}
                codeString={codeString}
                {...props}
              />
            );
          }

          return (
            <InlineCode {...props}>
              {React.Children.map(children, (child) =>
                typeof child === "string"
                  ? tokenizeTextToNodes(
                      child,
                      contextArray,
                      handleContextModalOpen
                    )
                  : child
              )}
            </InlineCode>
          );
        },

        // Inline elements
        strong: ({ children, ...props }) => (
          <strong style={{ fontWeight: "bold", color: "#000000" }} {...props}>
            {React.Children.map(children, (child) =>
              typeof child === "string"
                ? tokenizeTextToNodes(
                    child,
                    contextArray,
                    handleContextModalOpen
                  )
                : child
            )}
          </strong>
        ),
        em: ({ children, ...props }) => (
          <em style={{ fontStyle: "italic" }} {...props}>
            {React.Children.map(children, (child) =>
              typeof child === "string"
                ? tokenizeTextToNodes(
                    child,
                    contextArray,
                    handleContextModalOpen
                  )
                : child
            )}
          </em>
        ),
        del: ({ children, ...props }) => (
          <del style={{ textDecoration: "line-through" }} {...props}>
            {React.Children.map(children, (child) =>
              typeof child === "string"
                ? tokenizeTextToNodes(
                    child,
                    contextArray,
                    handleContextModalOpen
                  )
                : child
            )}
          </del>
        ),
        a: ({ children, href, ...props }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#000000", textDecoration: "underline" }}
            {...props}
          >
            {React.Children.map(children, (child) =>
              typeof child === "string"
                ? tokenizeTextToNodes(
                    child,
                    contextArray,
                    handleContextModalOpen
                  )
                : child
            )}
          </a>
        ),

        // Other elements
        hr: ({ node, ...props }) => (
          <hr
            style={{ border: "1px solid #e0e0e0", margin: "24px 0" }}
            {...props}
          />
        ),
        img: ({ node, src, alt, ...props }) => (
          <img
            src={src}
            alt={alt}
            style={{
              maxWidth: "100%",
              height: "auto",
              borderRadius: "8px",
              margin: "16px 0",
            }}
            {...props}
          />
        ),
      }),
      [contextArray, handleContextModalOpen]
    );

    return (
      <Box className="markdown-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkBreaks]}
          rehypePlugins={[rehypeHighlight, rehypeRaw, rehypeSanitize]}
          components={components}
          skipHtml={false}
          allowElement={(element, index, parent) => {
            return true;
          }}
        >
          {showInlineContext ? markdown : stripContextMarkers(markdown)}
        </ReactMarkdown>
      </Box>
    );
  }
);

CustomMarkdown.displayName = "CustomMarkdown";

export default CustomMarkdown;
